import React, { useState, useRef, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import DateTimePicker from '@react-native-community/datetimepicker';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Pressable,
  ScrollView,
  TextInput,
  Platform,
} from 'react-native';
import { Feather, Ionicons } from '@expo/vector-icons';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { localeFor } from '../../utils/dateLocale';
import { TFunction } from 'i18next';
import { RootState } from '../../redux/store';
import {
  selectRecipeCookHistory,
  selectMostRecentScheduledDate,
  selectPlannedMealsForRecipe,
  pickMealToBridgeCook,
  markMealAsCooked,
  rateMeal,
} from '../../redux/mealPlanSlice';
import {
  selectRecipeCookLog,
  addCookLogEntry,
  CookVerdict,
} from '../../redux/cookLogSlice';
import { selectRecipeMetadata, selectRecipeVerdicts } from '../../redux/recipeMetadataSlice';
import { summarizeCookJournal, captureNoteToSave } from '../../redux/cookNoteJournal';
import { verdictsAnsweredDuring } from './cookCaptureDismiss';
import { VerdictRows } from '../household/VerdictRows';
import { useHouseholdMembers } from '../../hooks/useHouseholdMembers';
import { toIsoDate, parseDayOrInstant } from '../../utils/localDate';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius, PaprikaShadows } from '../../styles/theme';

const isWeb = Platform.OS === 'web';

interface CookLogProps {
  recipeId: string;
}

/** Imperative handle so an external trigger (e.g. the top actions bar
 *  "J'ai cuisiné" button) can open the same capture sheet as the card. */
export interface CookLogHandle {
  openCapture: () => void;
}


function verdictFromRating(r: number): CookVerdict {
  if (r >= 5) return 'loved';
  if (r >= 4) return 'again';
  if (r >= 3) return 'ok';
  return 'meh';
}

/**
 * The locale is passed in rather than read from i18n here: this is a module-level
 * helper, so reading the language internally would bind it at import time and go
 * stale after a language switch. Callers hand over `i18n.language` from
 * `useTranslation()`, which re-renders on change. Same contract as
 * `src/utils/dateLocale.ts`.
 */
function formatFullDate(iso: string, language: string | undefined): string {
  try {
    return parseDayOrInstant(iso).toLocaleDateString(localeFor(language), {
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return '';
  }
}

// ---------- CookCapture sheet ----------

/** Payload emitted by the capture sheet when the user saves a cook. */
export interface CookCaptureEntry {
  rating: number;
  note?: string;
  date: string;
}

export function CookCapture({
  visible,
  recipeId,
  onClose,
  onSave,
  onDismissAnswered,
  defaultDate,
  initialNote,
  t,
  language,
}: {
  visible: boolean;
  /** Needed because the verdict rows read and write the household's answers
   *  for this recipe directly, rather than returning a value through onSave. */
  recipeId: string;
  onClose: () => void;
  onSave: (entry: CookCaptureEntry) => void;
  /**
   * The sheet was closed without « Enregistrer au journal » after a verdict
   * was answered during this opening (#865). The verdicts are already written;
   * the caller decides what the answer meant for the meal — the planner marks
   * it cooked on `date`, with no note. Called before `onClose`.
   */
  onDismissAnswered?: (entry: { date: string }) => void;
  defaultDate?: string;
  /**
   * The note already on the meal, when the sheet reopens a cooked one. Saving
   * replaces the meal's note, so an empty field let a second note silently
   * erase the first (found by /qa, #795): the sheet now opens on it, to edit.
   */
  initialNote?: string;
  t: TFunction;
  /** Active i18n language, for date formatting. Passed in, never read here — see formatFullDate. */
  language?: string;
}) {
  // Local-date fallback — never UTC (new Date().toISOString() day-shifts across
  // the timezone boundary and can show the wrong day).
  const today = toIsoDate(new Date());
  // The rating is no longer typed in here — it is derived from what each
  // person at the table said, so this sheet reads it back out of the store.
  const verdicts = useSelector((s: RootState) => selectRecipeVerdicts(s, recipeId));
  const rating = useSelector(
    (s: RootState) => selectRecipeMetadata(s, recipeId)?.rating ?? 0
  );
  const answered = Object.keys(verdicts).length > 0;
  // What the table had said when this opening began. A chip tapped since is
  // an answer about this meal; an answer from an earlier cook is not (#865).
  const verdictsAtOpen = useRef(verdicts);
  useEffect(() => {
    if (visible) verdictsAtOpen.current = verdicts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  const [note, setNote] = useState(initialNote ?? '');
  const [date, setDate] = useState(defaultDate ?? today);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateInputRef = useRef<any>(null);

  // When a single shared sheet is reused across meals (e.g. the weekly screen),
  // `useState(defaultDate)` only reads the first meal's date. Sync when the
  // prop changes so each meal opens on its own planned day.
  useEffect(() => {
    setDate(defaultDate ?? today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDate]);

  const reset = () => {
    // Verdicts are not reset: they belong to the recipe, not to this sheet,
    // and were already written the moment they were tapped.
    setNote(initialNote ?? '');
    setDate(defaultDate ?? today);
    setShowDatePicker(false);
  };

  const handleClose = () => {
    const answeredNow = verdictsAnsweredDuring(verdictsAtOpen.current, verdicts);
    reset();
    if (answeredNow) onDismissAnswered?.({ date });
    onClose();
  };

  const handleSave = () => {
    onSave({ rating: rating || 0, note: captureNoteToSave(note, initialNote), date });
    reset();
  };

  const dateLabel = date === today
    ? `${t('cookLog.today', "Aujourd'hui")} · ${formatFullDate(date, language)}`
    : formatFullDate(date, language);

  return (
    <Modal visible={visible} transparent animationType={isWeb ? 'fade' : 'slide'} onRequestClose={handleClose}>
      <Pressable style={isWeb ? styles.overlayWeb : styles.overlay} onPress={handleClose}>
        <Pressable testID="cook-capture-sheet" style={isWeb ? styles.captureSheetWeb : styles.captureSheet} onPress={(e) => e.stopPropagation()}>
          {!isWeb && (
            <View style={styles.handleContainer}>
              <View style={styles.handle} />
            </View>
          )}

          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.captureContent}>
            <Text style={styles.captureTitle}>{t('cookLog.captureTitle', "Comment c'était ?")}</Text>
            <Text style={styles.captureSubtitle}>{t('cookLog.captureSubtitle', "On l'ajoute à votre historique.")}</Text>

            {/* Who liked it — the same rows every other cook gesture shows */}
            <Text style={styles.sectionLabel}>{t('household.verdict.sectionLabel')}</Text>
            <View style={styles.captureVerdicts}>
              <VerdictRows recipeId={recipeId} surface="cook_log" />
            </View>

            {/* Date */}
            <Text style={styles.sectionLabel}>{t('cookLog.cookedOn', 'CUISINÉ LE')}</Text>
            {isWeb ? (
              // RNW Pressable blocks native input pointer events; call showPicker()
              // imperatively from onPress so the browser allows it (user-gesture context).
              <TouchableOpacity
                style={styles.dateButton}
                activeOpacity={0.75}
                onPress={() => dateInputRef.current?.showPicker?.()}
              >
                <Ionicons name="calendar-outline" size={16} color={Paprika.textSecondary} />
                <Text style={styles.dateButtonText}>{dateLabel}</Text>
                <Feather name="chevron-right" size={16} color={Paprika.textMuted} style={{ marginLeft: 'auto' }} />
                {/* @ts-ignore — native HTML input element */}
                <input
                  ref={dateInputRef}
                  type="date"
                  value={date}
                  onChange={(e: any) => setDate(e.target.value)}
                  style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                />
              </TouchableOpacity>
            ) : (
              <>
                <TouchableOpacity
                  style={styles.dateButton}
                  onPress={() => setShowDatePicker(true)}
                  activeOpacity={0.75}
                >
                  <Ionicons name="calendar-outline" size={16} color={Paprika.textSecondary} />
                  <Text style={styles.dateButtonText}>{dateLabel}</Text>
                  <Feather name="chevron-right" size={16} color={Paprika.textMuted} style={{ marginLeft: 'auto' }} />
                </TouchableOpacity>
                {showDatePicker && (
                  <DateTimePicker
                    value={new Date(date + 'T12:00:00')}
                    mode="date"
                    display="default"
                    onChange={(_event, selectedDate) => {
                      setShowDatePicker(false);
                      if (selectedDate) setDate(toIsoDate(selectedDate));
                    }}
                  />
                )}
              </>
            )}

            {/* Note */}
            <Text style={styles.sectionLabel}>
              {t('cookLog.noteLabel', 'UNE NOTE POUR LA PROCHAINE FOIS')}
              <Text style={styles.optionalLabel}> · {t('cookLog.optional', 'facultatif')}</Text>
            </Text>
            <TextInput
              testID="cook-capture-note"
              style={styles.noteInput}
              value={note}
              onChangeText={setNote}
              placeholder={t('cookLog.notePlaceholder', 'Doubler la vinaigrette la prochaine fois…')}
              placeholderTextColor={Paprika.textMuted}
              multiline
            />

            {/* Actions */}
            <View testID="cook-capture-actions" style={styles.captureActions}>
              <TouchableOpacity testID="cook-capture-cancel" style={styles.cancelButton} onPress={handleClose} activeOpacity={0.75}>
                <Text style={styles.cancelButtonText}>{t('cookLog.cancel', 'Annuler')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="cook-capture-save"
                style={[styles.saveButton, !answered && styles.saveButtonDisabled]}
                onPress={handleSave}
                disabled={!answered}
                activeOpacity={0.85}
              >
                <Feather name="check" size={16} color="#fff" />
                <Text style={styles.saveButtonText}>{t('cookLog.save', 'Enregistrer au journal')}</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ---------- Notes history ----------

/** Renders the free-form notes captured across past cooks, most recent first. */
// ---------- Main smart component ----------

export interface CookLogSummary {
  timesCooked: number;
  lastCooked?: string;
  verdict?: CookVerdict;
  /** Most recent first. `by` is a uid, absent on notes written before #662. */
  notes: Array<{ date: string; text: string; by?: string }>;
}

/**
 * Derives the cook journal for a recipe. Lives outside the component because
 * the recipe header renders the journal as a single line (`CookedLine`) while
 * `CookLog` itself now only owns the notes and the capture sheet.
 */
export function useCookLogSummary(recipeId: string): CookLogSummary {
  const mealHistory = useSelector((state: RootState) => selectRecipeCookHistory(state, recipeId));
  const cookLog = useSelector((state: RootState) => selectRecipeCookLog(state, recipeId));

  return useMemo(() => {
    // The plan and the journal together. A journal entry that copies a meal
    // still cooked in the plan is that same cook, counted and shown once; once
    // the meal is gone the copy is what keeps its note (#795).
    const { timesCooked, lastCooked, notes } = summarizeCookJournal(mealHistory, cookLog);
    return {
      timesCooked,
      lastCooked,
      verdict: cookLog[0]?.verdict,
      notes,
    };
    // No rating here any more: CookedLine shows the household's verdicts
    // directly, so nothing downstream needs the collapsed number.
  }, [mealHistory, cookLog]);
}

export const CookLog = forwardRef<CookLogHandle, CookLogProps>(({ recipeId }, ref) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { myUid } = useHouseholdMembers();
  const [captureOpen, setCaptureOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    openCapture: () => setCaptureOpen(true),
  }), []);

  const plannedMeals = useSelector((state: RootState) => selectPlannedMealsForRecipe(state, recipeId));
  const plannedDate = useSelector((state: RootState) => selectMostRecentScheduledDate(state, recipeId));

  const handleSave = (entry: { rating: number; note?: string; date: string }) => {
    const cookDate = new Date(entry.date + 'T12:00:00').toISOString();
    const now = new Date().toISOString();

    // Bridge the cook onto a planned meal so it surfaces in meal planning
    // (the weekly planner + recap read mealPlanSlice, not cookLogSlice).
    // Prefer an uncooked meal scheduled on the chosen date; otherwise the most
    // recently scheduled uncooked meal for this recipe. If none exists, fall
    // back to a standalone cook-log entry. Bridging (rather than also adding a
    // standalone entry) keeps `timesCooked` from double counting; the note is
    // preserved on the planned meal via markMealAsCooked's `note`.
    const matchedMeal = pickMealToBridgeCook(plannedMeals, entry.date);

    if (matchedMeal) {
      dispatch(markMealAsCooked({ mealId: matchedMeal.id, cookedAt: cookDate, note: entry.note, noteBy: myUid ?? undefined }));
      if (entry.rating > 0) {
        dispatch(rateMeal({ mealId: matchedMeal.id, rating: entry.rating }));
      }
    } else {
      dispatch(
        addCookLogEntry({
          id: `${recipeId}-${Date.now()}`,
          recipeId,
          date: cookDate,
          rating: entry.rating,
          verdict: verdictFromRating(entry.rating),
          note: entry.note,
          noteBy: myUid ?? undefined,
          createdAt: now,
        })
      );
    }
    // The canonical per-recipe rating is not written here any more: it is
    // derived from the household's verdicts, which VerdictRows already wrote.
    setCaptureOpen(false);
  };

  // No invite and no summary card any more: a recipe you have never cooked
  // shows nothing at all, and once cooked the journal is one line in the header
  // (`CookedLine`). What is left here is your own notes plus the capture sheet,
  // opened from the ⋯ menu via the imperative handle.
  // Nothing renders here any more except the sheet. The notes moved into the
  // header beside the verdicts: they are the same journal entry, and splitting
  // them put "why it went well" a full scroll below "who liked it".
  return (
    <View style={styles.container}>
      <CookCapture visible={captureOpen} recipeId={recipeId} onClose={() => setCaptureOpen(false)} onSave={handleSave} defaultDate={plannedDate} t={t} language={i18n.language} />
    </View>
  );
});

CookLog.displayName = 'CookLog';

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: PaprikaSpacing.md,
    paddingBottom: PaprikaSpacing.sm,
    maxWidth: 560,
  },

  // Notes history — tonal surface, warm shadow, no hard borders (DESIGN.md)

  // Invite
  inviteCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.surfaceLow,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.lg,
    padding: PaprikaSpacing.md,
  },
  inviteIconPill: {
    width: 40,
    height: 40,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.brandSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inviteTextBlock: {
    flex: 1,
    gap: 2,
  },
  inviteTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 18,
    lineHeight: 22,
    color: Paprika.textPrimary,
  },
  inviteSubtitle: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
  },
  inviteCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.xs,
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.full,
    paddingVertical: 9,
    paddingHorizontal: PaprikaSpacing.md,
  },
  inviteCtaText: {
    ...PaprikaTypography.labelLarge,
    color: '#fff',
  },

  // Filled
  filledCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.brandTint,
    borderWidth: 1,
    borderColor: Paprika.brandSoft,
    borderRadius: PaprikaRadius.lg,
    padding: PaprikaSpacing.md,
  },
  filledLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    flexShrink: 1,
  },
  filledIconPill: {
    width: 36,
    height: 36,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.brandSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filledTextBlock: {
    gap: 2,
    flexShrink: 1,
  },
  filledTitle: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
  },
  filledSub: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
  },
  filledRight: {
    alignItems: 'flex-end',
    gap: 2,
  },
  starRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  verdictText: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 15,
    color: Paprika.accent,
    marginTop: 1,
  },

  // Capture sheet
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(24, 16, 8, 0.34)',
    justifyContent: 'flex-end',
  },
  overlayWeb: {
    flex: 1,
    backgroundColor: 'rgba(24, 16, 8, 0.34)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: PaprikaSpacing.lg,
  },
  captureSheet: {
    backgroundColor: Paprika.background,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
    maxHeight: '88%',
    // Only the extra allowance a bottom sheet needs over the phone's bottom
    // edge. The 24 that frames the content itself lives on captureContent, so
    // native still totals the 32 it always had.
    paddingBottom: PaprikaSpacing.sm,
  },
  captureSheetWeb: {
    backgroundColor: Paprika.background,
    borderRadius: PaprikaRadius.lg,
    width: '100%',
    maxWidth: 460,
    maxHeight: '85%',
    ...PaprikaShadows.modal,
  },
  handleContainer: {
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.sm,
  },
  handle: {
    width: 36,
    height: 4,
    backgroundColor: Paprika.border,
    borderRadius: 2,
  },
  captureContent: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: PaprikaSpacing.md,
    // The bottom inset belongs on the content, not on the sheet: it was on
    // captureSheet only, captureSheetWeb was written without one, and on web
    // the actions row ended flush against the card's bottom edge. Here both
    // platforms inherit it, and it matches paddingHorizontal so the row sits
    // in an even frame.
    paddingBottom: PaprikaSpacing.lg,
    gap: PaprikaSpacing.xs,
  },
  captureTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 24,
    lineHeight: 30,
    color: Paprika.textPrimary,
  },
  captureSubtitle: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.sm,
  },
  sectionLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    letterSpacing: 0.6,
    marginTop: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.xs,
  },
  optionalLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    textTransform: 'none',
    fontWeight: '400',
  },
  captureVerdicts: {
    marginBottom: PaprikaSpacing.xs,
  },
  dateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    paddingVertical: 10,
    paddingHorizontal: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.cardBg,
  },
  dateButtonText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },
  noteInput: {
    minHeight: 66,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.cardBg,
    padding: PaprikaSpacing.md,
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    textAlignVertical: 'top',
  },
  captureActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    marginTop: PaprikaSpacing.lg,
  },
  cancelButton: {
    paddingVertical: 12,
    paddingHorizontal: PaprikaSpacing.lg,
    borderRadius: PaprikaRadius.full,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.cardBg,
  },
  cancelButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  saveButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.xs,
    paddingVertical: 12,
    borderRadius: PaprikaRadius.full,
    backgroundColor: Paprika.accent,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    ...PaprikaTypography.labelLarge,
    color: '#fff',
  },
});

export default CookLog;
